import { useCallback, useRef, useState } from "react";
import { esCedula, parseCedula } from "./cedula";

const SOLO_RUT = /^[0-9kK.\- ]*$/;

/** Lectura del QR de la cédula con un lector de códigos.
 *
 *  El lector emula teclado: escribe carácter por carácter y termina con Enter. Por eso no se
 *  puede formatear el texto como RUT mientras llega (quedaba "09.752.161.609…") ni intentar
 *  parsear en cada tecla (la palabra "docstatus" aparece al principio, cuando todavía no hay
 *  datos). Se deja el texto crudo y se procesa cuando el lector deja de escribir.
 */
export function useCedulaScan({ onCedula, onError, onEnter, delay = 300 }) {
  const [valor, setValor] = useState("");
  const timer = useRef(null);

  const procesar = useCallback((texto) => {
    clearTimeout(timer.current);
    const ced = parseCedula(texto);
    if (ced?.rut && ced.nacimiento) { onCedula(ced, texto); return true; }
    if (esCedula(texto) && /mrz/i.test(texto)) { onError?.(texto); return true; }
    return false;
  }, [onCedula, onError]);

  /** Devuelve true si el valor quedó capturado como lectura de cédula (crudo, sin formatear). */
  const alEscribir = useCallback((texto) => {
    if (SOLO_RUT.test(texto)) return false;       // es un RUT tecleado: lo maneja el componente
    setValor(texto);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => procesar(texto), delay);
    return true;
  }, [procesar, delay]);

  const alEnter = useCallback((texto) => {
    if (procesar(texto)) return true;
    onEnter?.(texto);
    return false;
  }, [procesar, onEnter]);

  const limpiar = useCallback(() => { clearTimeout(timer.current); setValor(""); }, []);

  return { valorCrudo: valor, alEscribir, alEnter, limpiar };
}
